/**
 * Render-gate system for Steve Render Service.
 *
 * When the headless browser captures a frame, it checks `window.ready`.
 * If any async work is pending (loading media, seeking video), call
 * `delayRender()` to block capture. Call `continueRender(handle)` when done.
 *
 * Written from scratch for Steve.AI — no third-party dependencies.
 */

import { getEnvironment } from './get-environment.js';
import { truthy } from './truthy.js';

// Start ready — the puppeteer renderer waits for this before capturing
if (typeof window !== 'undefined') {
  window.ready = true;
}

let pendingHandles = [];
const activeTimeouts = {};
const handleLabels = {}; // handle → label, for debugging

if (typeof window !== 'undefined') {
  window.picmaker_getPendingLabels = () => Object.values(handleLabels);
}

const DEFAULT_TIMEOUT_MS = 30000;

/**
 * Block the renderer from capturing the current frame.
 * Returns a handle that must be passed to `continueRender()` to unblock.
 *
 * @param {string} [label] - Optional description for debugging timeout errors.
 * @returns {number} handle
 */
export const delayRender = (label) => {
  if (label !== undefined && typeof label !== 'string') {
    throw new Error(
      'delayRender() label must be a string or undefined, got: ' + JSON.stringify(label)
    );
  }

  const handle = Math.random();
  pendingHandles.push(handle);
  if (label) handleLabels[handle] = label;

  // In rendering mode, set a timeout so hung renders don't block forever
  if (getEnvironment() === 'rendering') {
    const timeoutMs =
      typeof window !== 'undefined'
        ? (window.picmaker_puppeteerTimeout ?? DEFAULT_TIMEOUT_MS) - 2000
        : DEFAULT_TIMEOUT_MS;

    activeTimeouts[handle] = setTimeout(() => {
      const parts = [
        'delayRender()',
        label ? `"${label}"` : null,
        `was not resolved within ${timeoutMs}ms.`,
      ].filter(truthy);
      throw new Error(parts.join(' '));
    }, timeoutMs);
  }

  if (typeof window !== 'undefined') {
    window.ready = false;
  }

  return handle;
};

/**
 * Release a previously acquired render-gate handle.
 * When all handles are released, `window.ready` becomes true and
 * the renderer can proceed to capture the frame.
 *
 * @param {number} handle - The value returned by delayRender().
 */
export const continueRender = (handle) => {
  if (handle === undefined) {
    throw new TypeError(
      'continueRender() requires the handle returned by delayRender().'
    );
  }

  if (typeof handle !== 'number') {
    throw new TypeError(
      'continueRender() handle must be a number. Got: ' + JSON.stringify(handle)
    );
  }

  pendingHandles = pendingHandles.filter((h) => {
    if (h === handle) {
      if (activeTimeouts[handle] !== undefined) {
        clearTimeout(activeTimeouts[handle]);
        delete activeTimeouts[handle];
      }
      delete handleLabels[handle];
      return false;
    }
    return true;
  });

  if (pendingHandles.length === 0 && typeof window !== 'undefined') {
    window.ready = true;
    // Increment monotonic frame counter — used by renderer to poll for frame completion
    // without the race condition of window.ready briefly being true between frames.
    window.picmaker_frameCounter = (window.picmaker_frameCounter || 0) + 1;
    // Push notification so the renderer doesn't need to poll window.ready
    window.picmaker_frameReady?.();
  }
};
